/**
 * @file     useSignInLink.ts
 * @readme   ./README.md
 * @purpose  The header's Sign in link returns to the page it was clicked on.
 *           The rendered href is the bare sign-in route; the click adds the
 *           live path and query string, which map and filter syncs rewrite
 *           without a render.
 */
'use client';

import { useCallback, type FocusEvent, type MouseEvent, type PointerEvent } from 'react';
import { useRouter } from 'next/navigation';
import { routes, signInRoute } from '@/core/constants/routes';

// A per-page redirect in the rendered href gave crawlers one sign-in URL per
// page (~21k requests/day), and on rewritten pages it baked in the internal
// /properties-static path, which 404s after sign-in.
const href = routes.signIn;

export function useSignInLink() {
  const router = useRouter();

  const onClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const live = signInRoute(`${window.location.pathname}${window.location.search}`);
      if (live === href) return;
      event.preventDefault();
      router.push(live);
    },
    [router],
  );

  // Modified clicks, middle clicks and long-press "open in new tab" follow the
  // anchor's own href, so a person's pointer or focus writes the return path
  // onto it first. Crawlers never fire these, so they still see one URL.
  const primeHref = useCallback((event: PointerEvent<HTMLAnchorElement> | FocusEvent<HTMLAnchorElement>) => {
    event.currentTarget.href = signInRoute(`${window.location.pathname}${window.location.search}`);
  }, []);

  return { href, onClick, onPointerDown: primeHref, onFocus: primeHref };
}
